/**
 * LocalStateManager — persistent state wrapper around the browser's
 * localStorage API, with schema versioning and cross-version migrations.
 *
 * Used internally by the Adventure and Story Points managers to persist
 * user progress across sessions (completed tasks, heard story points,
 * last-used track/mode, etc.). Consumers of the library never touch this
 * directly — they consume the hooks which use this service transparently.
 *
 * Design constraints:
 *   - localStorage-only (no IndexedDB). Synchronous, simple, ~5 MB quota
 *     is plenty for thousands of story points and dozens of adventures.
 *   - Every value gets a `schema_version` field so we can migrate older
 *     payloads when the library ships a breaking state-shape change.
 *   - Namespaced keys (`audio-guide:{scope}:{id}`) so multiple libraries
 *     on the same domain don't collide.
 *   - Graceful degradation: in private-browsing mode or when quota is
 *     exceeded, read/write silently fall back to in-memory storage for
 *     the current session. The user still has a working app, they just
 *     lose persistence at tab-close time.
 */
const KEY_PREFIX = 'audio-guide:';
export class LocalStateManager {
    constructor() {
        this._inMemoryFallback = new Map();
        this._migrations = {};
        this._localStorageAvailable = LocalStateManager._probeLocalStorage();
    }
    /**
     * Register a migration for a specific scope. When `load()` encounters
     * a state with `schema_version` less than `toVersion`, the migration
     * is run to produce the new state. Multiple migrations can chain.
     *
     * Example:
     *   mgr.registerMigration('adventure', 1, 2, (old) => ({
     *     ...old,
     *     schema_version: 2,
     *     newField: 'default',
     *   }));
     */
    registerMigration(scope, fromVersion, toVersion, migrate) {
        const key = `${scope}:${fromVersion}->${toVersion}`;
        this._migrations[key] = migrate;
    }
    /**
     * Load a state object for a given scope + id. Returns null if nothing
     * is stored. If the stored schema_version is older than `currentVersion`,
     * runs migrations in sequence until caught up. If migration fails, the
     * corrupted entry is deleted and null is returned.
     */
    load(scope, id, currentVersion) {
        const key = this._buildKey(scope, id);
        const raw = this._rawGet(key);
        if (!raw)
            return null;
        try {
            let state = JSON.parse(raw);
            while (typeof state.schema_version === 'number' &&
                state.schema_version < currentVersion) {
                const migrationKey = `${scope}:${state.schema_version}->${state.schema_version + 1}`;
                const migrate = this._migrations[migrationKey];
                if (!migrate) {
                    // Missing migration — bail out, treat as stale state
                    this._rawDelete(key);
                    return null;
                }
                state = migrate(state);
            }
            return state;
        }
        catch {
            this._rawDelete(key);
            return null;
        }
    }
    /**
     * Save a state object for a given scope + id. The value MUST include
     * a schema_version field. Returns true on success, false on failure
     * (quota exceeded, storage unavailable, etc.).
     */
    save(scope, id, state) {
        const key = this._buildKey(scope, id);
        try {
            this._rawSet(key, JSON.stringify(state));
            return true;
        }
        catch {
            return false;
        }
    }
    /** Delete a single state entry. */
    remove(scope, id) {
        this._rawDelete(this._buildKey(scope, id));
    }
    /**
     * Bulk delete. Scope 'all' removes every key prefixed with KEY_PREFIX;
     * a specific scope (e.g. 'adventure') only removes that scope's entries.
     */
    clear(scope = 'all') {
        if (!this._localStorageAvailable) {
            if (scope === 'all') {
                this._inMemoryFallback.clear();
            }
            else {
                const prefix = `${KEY_PREFIX}${scope}:`;
                for (const key of Array.from(this._inMemoryFallback.keys())) {
                    if (key.startsWith(prefix))
                        this._inMemoryFallback.delete(key);
                }
            }
            return;
        }
        try {
            const prefix = scope === 'all' ? KEY_PREFIX : `${KEY_PREFIX}${scope}:`;
            const toDelete = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(prefix))
                    toDelete.push(key);
            }
            toDelete.forEach((k) => localStorage.removeItem(k));
        }
        catch {
            // silent — nothing we can do if localStorage is sealed off
        }
    }
    /** List all ids known for a given scope. Useful for "resume session" UX. */
    listIds(scope) {
        const prefix = `${KEY_PREFIX}${scope}:`;
        const ids = [];
        if (!this._localStorageAvailable) {
            for (const key of this._inMemoryFallback.keys()) {
                if (key.startsWith(prefix))
                    ids.push(key.slice(prefix.length));
            }
            return ids;
        }
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(prefix)) {
                    ids.push(key.slice(prefix.length));
                }
            }
        }
        catch {
            // silent
        }
        return ids;
    }
    // ──────────────────────────────────────────────────────────────
    // Private helpers
    // ──────────────────────────────────────────────────────────────
    _buildKey(scope, id) {
        return `${KEY_PREFIX}${scope}:${id}`;
    }
    _rawGet(key) {
        if (!this._localStorageAvailable) {
            return this._inMemoryFallback.get(key) ?? null;
        }
        try {
            return localStorage.getItem(key);
        }
        catch {
            return this._inMemoryFallback.get(key) ?? null;
        }
    }
    _rawSet(key, value) {
        if (this._localStorageAvailable) {
            try {
                localStorage.setItem(key, value);
                return;
            }
            catch {
                // quota exceeded or sealed storage — fall through to in-memory
            }
        }
        this._inMemoryFallback.set(key, value);
    }
    _rawDelete(key) {
        this._inMemoryFallback.delete(key);
        if (this._localStorageAvailable) {
            try {
                localStorage.removeItem(key);
            }
            catch {
                // silent
            }
        }
    }
    /**
     * Probe whether localStorage is actually usable at construction time.
     * Private browsing in older Safari can expose localStorage but throw
     * on every write — this probe catches that.
     */
    static _probeLocalStorage() {
        if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
            return false;
        }
        try {
            const probeKey = `${KEY_PREFIX}__probe__`;
            localStorage.setItem(probeKey, '1');
            localStorage.removeItem(probeKey);
            return true;
        }
        catch {
            return false;
        }
    }
}
//# sourceMappingURL=LocalStateManager.js.map